import {
  ChangeDetectionStrategy, ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy, Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import {Order, OrderResultInformation} from "../../models/order";
import {Subject, Subscription} from "rxjs";
import {DxDataGridComponent} from "devextreme-angular";
import {OrderService} from "../../services/order.service";
import {take} from "rxjs/operators";
import {difference, flatten, intersection} from "lodash";
import {alert, confirm} from "devextreme/ui/dialog";
import {OrdersDictionaryService} from "./orders-dictionary.service";
import {NormalTest} from "../../models/test";
import {HttpResponse, HttpStatusCode} from "@angular/common/http";
import {OrderTest} from "../../models/order-test";
import DataSource from "devextreme/data/data_source";
import ArrayStore from "devextreme/data/array_store";
import {ValueChangedEvent} from "devextreme/ui/check_box";
import {ResultType} from "../../enums/result-type";
import {ResultMode} from "../../enums/result-mode";
import {NumericClinicalRange} from "../../models/clinical-ranges/numeric-clinical-range";
import {AlphaClinicalRange} from "../../models/clinical-ranges/alpha-clinical-range";
import {TiterClinicalRange} from "../../models/clinical-ranges/titer-clinical-range";
import {OpenClinicalRange} from "../../models/clinical-ranges/open-clinical-range";
import {SpecificCutOffClinicalRange} from "../../models/clinical-ranges/specific-cut-off-clinical-range";
import {PatientHelpers} from "../../helpers/patient-helpers";
import {Gender} from "../../enums/gender";
import {Age} from "../../models/clinical-ranges/age";
import {ResultFlag, ResultFlagNumeric, ResultFlagNumericConverter} from "../../enums/result-flag";
import {SpecificCutSign} from "../../enums/specific-cut-sign";
import {SpecificCutOffFlag} from "../../enums/specific-cut-off-flag";
import {CellClickEvent, FocusedCellChangedEvent, RowValidatingEvent, SavingEvent} from "devextreme/ui/data_grid";
import {ValidationCallbackData} from "devextreme/ui/validation_rules";
import {FormControl} from "@angular/forms";
import {DomSanitizer, SafeHtml} from "@angular/platform-browser";
import {NormalTestRun, PreviewNormalTestRun} from "../../models/test-run";
import notify from "devextreme/ui/notify";
import {KeyDownEvent} from "devextreme/ui/select_box";
import DevExpress from "devextreme";
import CellInfo = DevExpress.ui.dxTreeList.CellInfo;
import {FileUtils} from "../../../../../shared/utils";
import {ReportsService} from "../../../../shared/services/reports.service";
import {PWADetectorService} from "../../../../../shared/services/general";
import {ClientTests} from "../../models/client-tests";

class GridData {
  id: string;
  sort: number;
  orderId: number;
  resultScale: number;
  orderCode: string;
  testId: number;
  editing: boolean = false;
  testCode: string;
  testName: string;
  result?: string;
  cutOff?: string;
  reportAs?: string;
  editable: boolean = false;
  modified?: boolean = false;
  flag?: string;
  referenceRange?: string;
  unitOfMeasure?: string;
  comments?: string;
  resultType?: ResultType;
  mode?: ResultMode;
  clinicalRange?: NumericClinicalRange | AlphaClinicalRange | TiterClinicalRange | OpenClinicalRange | SpecificCutOffClinicalRange;

  constructor(item: GridData) {
    this.id = item.id;
    this.sort = item.sort;
    this.resultScale = item.resultScale;
    this.editing = item.editing;
    this.orderId = item.orderId;
    this.orderCode = item.orderCode;
    this.editable = item.editable;
    this.testId = item.testId;
    this.testCode = item.testCode;
    this.testName = item.testName;
    this.result = item.result ? item.result : '';
    this.flag = item.flag ? item.flag : '';
    this.comments = item.comments ? item.comments : '';
    this.unitOfMeasure = item.unitOfMeasure ? item.unitOfMeasure : '';
    this.resultType = item.resultType ? item.resultType : ResultType.None;
    this.mode = item.mode ? item.mode : ResultMode.None;
    this.clinicalRange = item.clinicalRange ? item.clinicalRange : undefined;
    this.referenceRange = item.referenceRange;
    this.cutOff = item.cutOff;
  }
}

@Component({
  selector: 'app-result-manager',
  templateUrl: './result-manager.component.html',
  styleUrls: ['./result-manager.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ResultManagerComponent implements OnChanges, OnDestroy {
  @Input() public orders: Order[] = [];
  @Output() public resultsSaved: EventEmitter<void> = new EventEmitter<void>();
  @ViewChild(DxDataGridComponent, {static: false}) public dxDataGridComponent?: DxDataGridComponent;

  public showPendingResults?: boolean = false;
  public showOnlyWithAccess?: boolean = false;
  public gridData = new DataSource({
    store: new ArrayStore({
      key: 'id'
    })
  });
  public changes: GridData[] = [];
  public selectedKeys: any[] = [];
  public commentsControl: FormControl = new FormControl();
  public titerRegex = /^\d{1,3}(:\d+)$/
  public isDialogVisible = false;
  public isCommentsDialogVisible = false;
  public valid = false;
  private requestSubscription?: Subscription;
  private tests: NormalTest[] = [];
  private clientsTests: ClientTests[] = [];
  private ordersInformation: OrderResultInformation[] = [];
  private onDestroy$: Subject<void> = new Subject();

  constructor(private pwaDetectorService: PWADetectorService, private reportsService: ReportsService, private orderService: OrderService, private orderDictionary: OrdersDictionaryService, private htmlSanitizer: DomSanitizer, private ref: ChangeDetectorRef) {
    this.performRequest = this.performRequest.bind(this);
    this.validateItem = this.validateItem.bind(this);
    this.save = this.save.bind(this);
    this.onCommentsDialogClose = this.onCommentsDialogClose.bind(this);
    this.onDialogClose = this.onDialogClose.bind(this);
  }

  ngOnChanges(changes: SimpleChanges): void {
    this.handleOrdersSelectionChanges(changes);
  }

  ngOnDestroy(): void {
    this.destroyPreviousSubscription();
    this.onDestroy$.next();
    this.onDestroy$.complete();
  }

  private performRequest(conf: { clientsIds: number[], orderIds: number[], testIds: number[], showOnlyPending: boolean }, callback: (res: HttpResponse<{ testsConfigurations: NormalTest[], orderResultsCalculationInformation: OrderResultInformation[] }>) => void) {
    this.dxDataGridComponent?.instance.beginCustomLoading("Loading orders data...");
    this.requestSubscription = this.orderService.getOrdersConfigurations(conf)
      .pipe(take(1))
      // @ts-ignore
      .subscribe(callback);
  }

  //region order events methods

  public changeCheckBoxValue(event: ValueChangedEvent) {
    this.showPendingResults = event.value;
    this.performRequest({
        orderIds: this.orders.map(o => o.id),
        testIds: this.tests.map(t => t.id),
        clientsIds: this.orders.map(o => o.clientId),
        showOnlyPending: this.showPendingResults as boolean
      },
      res => {
        try {
          if (res.status !== HttpStatusCode.Ok) return;
          const {
            clientsConfigurations,
            testsConfigurations,
            orderResultsCalculationInformation
          } = res.body as { clientsConfigurations: ClientTests[], testsConfigurations: NormalTest[], orderResultsCalculationInformation: OrderResultInformation[] };
          this.clientsTests = clientsConfigurations;
          this.showOnlyWithAccess = false;
          this.addTests(testsConfigurations);
          this.addOrdersInformation(orderResultsCalculationInformation);
          this.handleGridLoad().then().catch(e => console.log(e));
          this.dxDataGridComponent?.instance.endCustomLoading();
        } catch {
          alert("There was an error trying to load orders information. Please, try to search again.", "Loading orders error").then().catch(e => console.log(e));
        }
      });
  }

  public handleOrdersSelectionChanges(changes: SimpleChanges) {
    if (changes.orders && changes.orders.currentValue) {
      if (changes.orders.currentValue.length === 0) {
        this.clearAll();
        this.handleGridLoad().then().catch(e => console.log(e));
        return;
      }

      const newOrders = difference(changes.orders.currentValue.map((o: Order) => o.id), this.ordersInformation.map(o => o.orderId));
      const queueResponse = this.orderDictionary.enqueueOrders(changes.orders.currentValue.filter((o: Order) => newOrders.includes(o.id)));

      if (queueResponse.removed && queueResponse.removed.length > 0) {
        this.removeOrders(queueResponse.removed);
      }

      this.handlerOrderQueueChanges();
    }
  }

  private destroyPreviousSubscription() {
    if (this.requestSubscription && !this.requestSubscription.closed) {
      this.requestSubscription.unsubscribe();
    }
  }

  private removeOrders(ordersIds: number[]) {
    for (const orderId of ordersIds) {
      const index = this.ordersInformation.findIndex(o => o.orderId === orderId);
      const removedOrders = this.ordersInformation.splice(index, 1);

      if (removedOrders && removedOrders.length === 1) {
        const testsIds = removedOrders[0].orderTests?.map(t => t.id);
        this.removeTests(testsIds);
      }
    }
  }

  private removeTests(testsIds: number[]) {
    for (const testsId of testsIds) {
      const ordersTests = flatten(this.ordersInformation.map(o => o.orderTests)) as OrderTest[];
      const ordersTestsIds = ordersTests.map(o => o.testId);
      if (!ordersTestsIds.includes(testsId)) {
        const index = this.tests.findIndex(t => t.id === testsId);
        this.tests.splice(index, 1);
      }
    }
  }

  private clearAll() {
    this.ordersInformation = [];
    this.tests = [];
  }

  private addTests(tests: NormalTest[]) {
    const idsToAdd = difference(tests.map(t => t.id), this.tests.map(t => t.id));
    const testsToAdd = tests.filter(t => idsToAdd.includes(t.id));

    this.tests.push(...testsToAdd);
  }

  private addOrdersInformation(ordersInformation: OrderResultInformation[]) {
    const idsToAdd = difference(ordersInformation.map(o => o.orderId), this.ordersInformation.map(t => t.orderId));
    const idsToUpdate = intersection(ordersInformation.map(o => o.orderId), this.ordersInformation.map(t => t.orderId));

    for (const toUpdate of idsToUpdate) {
      const index = this.ordersInformation.findIndex(o => o.orderId === toUpdate);
      this.ordersInformation[index] = ordersInformation.find(o => o.orderId === toUpdate) as OrderResultInformation;
    }

    const informationToAdd = ordersInformation.filter(o => idsToAdd.includes(o.orderId));
    this.ordersInformation.push(...informationToAdd);
  }

  private handlerOrderQueueChanges() {
    this.destroyPreviousSubscription();

    const orders = this.orderDictionary.Orders;

    this.performRequest({
      orderIds: orders.map(o => o.id),
      testIds: this.tests.map(t => t.id),
      clientsIds: this.orders.map(o => o.clientId),
      showOnlyPending: this.showPendingResults as boolean
    }, res => {
      try {
        if (res.status !== HttpStatusCode.Ok) return;
        const {
          clientsConfigurations,
          testsConfigurations,
          orderResultsCalculationInformation
        } = res.body as { clientsConfigurations: ClientTests[], testsConfigurations: NormalTest[], orderResultsCalculationInformation: OrderResultInformation[] };
        this.clientsTests = clientsConfigurations;
        this.showOnlyWithAccess = false;
        this.addTests(testsConfigurations);
        this.addOrdersInformation(orderResultsCalculationInformation);
        this.orderDictionary.dequeueOrders(orders);
        this.handleGridLoad().then();
        this.dxDataGridComponent?.instance.endCustomLoading();
      } catch {
        alert("There was an error trying to load orders information. Please, try to search again.", "Loading orders error").then().catch(e => console.log(e));
      }
    });
  }

  //endregion

  //region grid events methods

  public cellFocused(event: CellClickEvent) {
    if (event.rowType !== 'data') return;
    const isTouchDevice = () => {
      return (('ontouchstart' in window) ||
        (navigator.maxTouchPoints > 0) ||
        (navigator.msMaxTouchPoints > 0));
    }
    if (!isTouchDevice()) {
      const cell = this.dxDataGridComponent?.instance.getCellElement(event.rowIndex, event.column?.dataField as string);
      this.dxDataGridComponent?.instance.focus(cell as HTMLElement);
    }
  }

  public numericControlEnterKey(event: KeyDownEvent, cellData: CellInfo) {
    if ((event as any).code === 'Enter') {
      const cell = this.dxDataGridComponent?.instance.getCellElement(cellData.rowIndex + 1, cellData.column.dataField as string);
      this.dxDataGridComponent?.instance.focus(cell as HTMLElement);
    }
  }

  public alphaControlEnterKey(event: KeyDownEvent, cellData: CellInfo) {
    const cell = this.dxDataGridComponent?.instance.getCellElement(cellData.rowIndex + 1, cellData.column.dataField as string);
    this.dxDataGridComponent?.instance.focus(cell as HTMLElement);
  }

  public alphaControlKeyPressed({event, component}: KeyDownEvent, cellInfo: CellInfo) {
    const key = (event as any)?.key;
    if ((event as any).code === 'Enter') return this.alphaControlEnterKey({event, component} as KeyDownEvent, cellInfo);
    const cellData: GridData = cellInfo.data;
    const test = this.tests.find(t => t.id === cellData.testId);
    if (!test) return;
    const clinicalRanges = test.alphaClinicalRanges.filter(cr => cr.result.toUpperCase().startsWith(key.toUpperCase()));
    if (clinicalRanges.length === 1) {
      cellData.result = clinicalRanges[0]?.result
      component.option("value", cellData.result);
    }
  }

  public validateItem(event: ValidationCallbackData) {
    if (event.value === '') return true;

    switch ((event.data as GridData).resultType) {
      case ResultType.ValueValue:
      case ResultType.GreaterEqualValue:
      case ResultType.LessEqualValue:
      case ResultType.GreaterThanValue:
      case ResultType.LessThanValue: {
        const res = Number(event.value);
        const message = res ? "" : "The result value is not valid.";
        event.validator.option("validationRules[0].message", message);

        return res;
      }
      case ResultType.AlphaResults:
        return true;
      case ResultType.OpenResultWithUnit:
      case ResultType.OpenResultWithoutUnit:
        return true;
      case ResultType.Titer: {
        const res = this.titerRegex.test(event.value as string);
        const message = !res ? "" : "The titer value is not valid.";
        event.validator.option("validationRules[0].message", message);

        return res;
      }
      case ResultType.SpecificCutOff: {
        const res = Number(event.value);
        const message = res ? "" : "The result value is not valid.";
        event.validator.option("validationRules[0].message", message);

        return res;
      }
    }

    return true;
  }

  public onSaving(event: SavingEvent) {
    const changes = event.changes.map(c => (c.data as GridData));

    for (const change of changes) {
      const index = this.changes.findIndex(c => c.id === change.id);
      if (index === -1) {
        this.changes.push(change);
        continue;
      }
      if (change.result === '') {
        this.changes.splice(index, 1);
        continue;
      }
      this.changes[index] = change;
    }
  }

  public onRowValidation(event: RowValidatingEvent) {
    this.valid = event.isValid;
  }

  private async handleGridLoad() {
    const gridDataOrderIds = this.gridData.store().createQuery().toArray().map((d: GridData) => d.orderId);
    const changesOrderIds = this.ordersInformation.map((o: OrderResultInformation) => o.orderId);

    const toRemove = difference(gridDataOrderIds, changesOrderIds) as number[];
    const toAdd = difference(changesOrderIds, gridDataOrderIds) as number[];
    const toUpdate = intersection(changesOrderIds, gridDataOrderIds) as number[];

    this.removeFromGrid(toRemove).then().catch(e => console.log(e));
    this.addToGrid(toAdd).then().catch(e => console.log(e));
    this.updateToGrid(toUpdate).then().catch(e => console.log(e));

    await this.gridData.reload();
    this.ref.detectChanges();
  }

  private getGridKey(orderId: string | number, testId: string | number) {
    return `${orderId}_${testId}`;
  }

  private async removeFromGrid(toRemoveIds: number[]) {
    const gridData = this.gridData.store().createQuery().toArray().filter((d: GridData) => toRemoveIds.includes(d.orderId)) as GridData[];

    for (const data of gridData) {
      await this.gridData.store().remove(this.getGridKey(data.orderId, data.testId));
    }
  }

  private async addToGrid(toAddIds: number[]) {
    const ordersInformation = this.ordersInformation.filter(o => toAddIds.includes(o.orderId));
    for (const info of ordersInformation) {
      const order = this.orders.find(o => o.id === info.orderId);

      if (!order) continue;

      for (const orderTest of info.orderTests) {
        const test = this.tests.find(t => t.id === orderTest.testId);

        if (!test) continue;

        const resultConfiguration = this.configureResult(info, orderTest, test);
        const client = this.clientsTests.find(c => c.clientId === order.clientId);
        const clientCanAccessTest = client?.testsIds.includes(test.id) || false;

        const data = new GridData({
          id: this.getGridKey(info.orderId, test.id),
          testId: test.id,
          sort: test.sort,
          orderId: info.orderId,
          resultScale: test.resultScale,
          testName: test.name,
          testCode: test.code,
          editable: clientCanAccessTest,
          orderCode: order.code,
          result: orderTest.validRun ? orderTest.validRun.result : '',
          resultType: test.resultType,
          editing: orderTest.validRun !== undefined && orderTest.validRun !== null,
          comments: orderTest.validRun ? orderTest.validRun.comments : '',
          flag: orderTest.validRun ? orderTest.validRun.flag?.toUpperCase() : '',
          unitOfMeasure: resultConfiguration.unitOfMeasure, // TODO CHECK WHAT HAPPENS WHEN IT'S BEING UPDATED
          clinicalRange: resultConfiguration.clinicalRange,
          referenceRange: resultConfiguration.refRange, // TODO CHECK WHAT HAPPENS WHEN IT'S BEING UPDATED
          mode: resultConfiguration.mode
        });

        await this.gridData.store().insert(data);
      }
    }
  }

  public sanitizeComments(cellData: GridData): string | SafeHtml {
    if (!cellData) return '';
    if (!cellData.comments || cellData.comments === '') return '';
    return this.htmlSanitizer.bypassSecurityTrustHtml(cellData.comments.toUpperCase());
  }

  private async updateToGrid(toUpdate: number[]) {
    await this.removeFromGrid(toUpdate);
    await this.addToGrid(toUpdate);
  }

  //endregion

  //region buttons events

  public showOnlyWithClientAccess(event: ValueChangedEvent) {
    this.showOnlyWithAccess = event.value;
    if (this.showOnlyWithAccess) {
      this.dxDataGridComponent?.instance.filter(["editable", '=', true]);
      this.dxDataGridComponent?.instance.refresh();
    } else {
      this.dxDataGridComponent?.instance.clearFilter();
      this.dxDataGridComponent?.instance.refresh();
    }
  }

  public async clear() {
    const result = await confirm("Are you sure you want to clear all results?", "Confirm Clear");
    if (result)
      this.performRequest({
          orderIds: this.orders.map(o => o.id),
          testIds: this.tests.map(t => t.id),
          clientsIds: this.orders.map(o => o.clientId),
          showOnlyPending: this.showPendingResults as boolean
        },
        res => {
          try {
            if (res.status !== HttpStatusCode.Ok) return;
            const {
              clientsConfigurations,
              testsConfigurations,
              orderResultsCalculationInformation
            } = res.body as { clientsConfigurations: ClientTests[], testsConfigurations: NormalTest[], orderResultsCalculationInformation: OrderResultInformation[] };
            this.clientsTests = clientsConfigurations;
            this.addTests(testsConfigurations);
            this.commentsControl.reset();
            this.showOnlyWithAccess = false;
            this.addOrdersInformation(orderResultsCalculationInformation);
            this.handleGridLoad();
            this.dxDataGridComponent?.instance.endCustomLoading();
          } catch {
            alert("There was an error trying to load orders information. Please, try to search again.", "Loading orders error").then().catch(e => console.log(e));
          }
        });
  }

  public showColumnChooser() {
    this.dxDataGridComponent?.instance.showColumnChooser();
  }

  public format(data: GridData) {
    const decimalPlaces = data.resultScale >= 0 ? data.resultScale : 9;
    let format = decimalPlaces === 0 ? "#0" : "#0.";
    for (let i = 0; i < decimalPlaces; i++) {
      format += "0#0";
    }
    return format;
  }

  public save() {
    // TODO CHECK IF A RESULT IS BEING UPDATED AND SHOW DIALOG

    const withValidResults: GridData[] = (this.dxDataGridComponent?.instance.getDataSource()
      .store() as ArrayStore).createQuery()
      .toArray()
      .filter((data: GridData) => data.result !== undefined && data.result !== null && data.result !== '' && data.editable && data.modified);
    this.dxDataGridComponent?.instance.beginCustomLoading("Saving results data...");
    const results: NormalTestRun[] = [];
    for (const withValidResult of withValidResults) {
      const order = this.ordersInformation.find(o => o.orderId === withValidResult.orderId);
      if (!order) continue;
      const orderTest = order.orderTests.find(ot => ot.testId === withValidResult.testId);
      if (!orderTest) continue;
      const testRun = new NormalTestRun(orderTest.id, false, (orderTest.validRun ? orderTest.validRun.runNumber : 1), withValidResult.result as string,
        new Date(), withValidResult.comments as string, withValidResult.flag as string, withValidResult.reportAs as string, withValidResult.cutOff as string);
      results.push(testRun);
    }
    this.orderService.saveResults(results)
      .pipe(take(1))
      .subscribe(async res => {
        this.dxDataGridComponent?.instance.endCustomLoading();
        if (res.status === HttpStatusCode.Ok) {
          notify("Results saved.", "success");

          for (const result of withValidResults) {
            await (this.dxDataGridComponent?.instance.getDataSource().store() as ArrayStore).remove(this.getGridKey(result.orderId, result.testId));
          }
          await this.gridData.reload();
          this.dxDataGridComponent?.instance.clearSelection();
          this.resultsSaved.emit();
        } else {
          notify("There was an error saving the results, check if there is data entry errors and then try again.", "error");
        }
      });
  }

  public preview() {
    const withValidResults: GridData[] = (this.dxDataGridComponent?.instance.getDataSource()
      .store() as ArrayStore).createQuery()
      .toArray().filter((data: GridData) => data.result !== undefined && data.result !== null && data.result !== '');
    this.dxDataGridComponent?.instance.beginCustomLoading("Generating preview report...");
    const results: PreviewNormalTestRun[] = [];
    for (const withValidResult of withValidResults) {
      const order = this.ordersInformation.find(o => o.orderId === withValidResult.orderId);
      if (!order) continue;
      const orderTest = order.orderTests.find(ot => ot.testId === withValidResult.testId);
      if (!orderTest) continue;
      const test = this.tests.find(t => t.id === orderTest.testId);
      if (!test) continue;

      const testRun = new PreviewNormalTestRun(order.orderId, test.id, withValidResult.unitOfMeasure as string, withValidResult.referenceRange as string,
        withValidResult.clinicalRange?.reportComment as string, test.resultType, orderTest.id, true, 1, withValidResult.result as string, new Date(),
        withValidResult.comments as string, withValidResult.flag as string, withValidResult.reportAs as string, withValidResult.cutOff as string);
      testRun.flag = this.calculateFlag(withValidResult);

      results.push(testRun);
    }

    this.reportsService.generateOrderPreview(results)
      .pipe(take(1))
      .subscribe((blob) => FileUtils.pdfRequestHandler(blob, this, true));
  }

  //endregion

  //region result methods

  private getNumericClinicalRange(orderInformation: OrderResultInformation, test: NormalTest): NumericClinicalRange | undefined {
    const ranges = test.numericClinicalRanges
      .filter(cr => new Date(cr.effectiveDate) <= new Date())
      .sort((a, b) => new Date(b.effectiveDate).getUTCDate() - new Date(a.effectiveDate).getUTCDate())
    if (ranges.length === 0) return undefined;

    let matchesByAge: NumericClinicalRange[];
    const patientAge = PatientHelpers.calculateAge(orderInformation.patientBirthDate);

    if (orderInformation.patientBirthDate)
      matchesByAge = ranges.filter(cr => (Age.isZero(cr.minAge) || Age.lessOrEqualsThan(cr.minAge, patientAge)) && (Age.isZero(cr.maxAge) || Age.greaterOrEqualsTo(cr.maxAge, patientAge)));
    else {
      matchesByAge = ranges.filter(cr => Age.isZero(cr.minAge) && Age.isZero(cr.maxAge));
      if (matchesByAge.length === 0) {
        const min = Math.min(...ranges.map(r => r.normalFrom));
        const max = Math.max(...ranges.map(r => r.normalTo));
        return new NumericClinicalRange({
          normalFrom: min,
          normalTo: max,
          gender: Gender.Any,
        } as NumericClinicalRange);
      }
    }
    return this.clinicalRangeByGender(matchesByAge, orderInformation);
  }

  private getAlphaClinicalRange(test: NormalTest): AlphaClinicalRange | undefined {
    const ranges = test.alphaClinicalRanges
      .filter(cr => new Date(cr.effectiveDate) <= new Date())
      .sort((a, b) => new Date(b.effectiveDate).getUTCDate() - new Date(a.effectiveDate).getUTCDate())

    return ranges.find(cr => cr);
  }

  private getOpenClinicalRange(test: NormalTest): OpenClinicalRange | undefined {
    const ranges = test.openClinicalRanges
      .filter(cr => new Date(cr.effectiveDate) <= new Date())
      .sort((a, b) => new Date(b.effectiveDate).getUTCDate() - new Date(a.effectiveDate).getUTCDate())

    return ranges.find(cr => cr);
  }

  private getTiterClinicalRange(orderInformation: OrderResultInformation, test: NormalTest): TiterClinicalRange | undefined {
    const ranges = test.titerClinicalRanges.filter(c => new Date(c.effectiveDate) <= new Date())
      .sort((a, b) => new Date(b.effectiveDate).getUTCDate() - new Date(a.effectiveDate).getUTCDate())

    if (ranges.length === 0) return undefined;

    let matchesByAge: TiterClinicalRange[];
    const patientAge = PatientHelpers.calculateAge(orderInformation.patientBirthDate);

    if (orderInformation.patientBirthDate)
      matchesByAge = ranges.filter(cr => (Age.isZero(cr.minAge) || Age.lessOrEqualsThan(cr.minAge, patientAge))
        && (Age.isZero(cr.maxAge) || Age.greaterOrEqualsTo(cr.maxAge, patientAge)));
    else {
      matchesByAge = ranges.filter(cr => Age.isZero(cr.minAge) && Age.isZero(cr.maxAge));
      if (matchesByAge.length === 0) {
        const min = ranges.map(r => r.normal).sort().find(r => r);
        const limit = ranges.map(r => {
          return {normal: r.normal, normalSign: r.normalSign}
        }).find(r => r.normal === min);

        return new TiterClinicalRange({
          normalSign: limit?.normalSign,
          normal: limit?.normal,
          gender: Gender.Any,
        } as TiterClinicalRange);
      }
    }
    return this.clinicalRangeByGender(matchesByAge, orderInformation);
  }

  private clinicalRangeByGender(matchesByAge: any[], orderInformation: OrderResultInformation) {
    const matchesForGenderAny = matchesByAge.find(cr => cr.gender === Gender.Any);
    if (!orderInformation.patientGender)
      return matchesForGenderAny;

    switch (orderInformation.patientGender.toUpperCase()) {
      case 'MALE':
        const matchesForGenderMale = matchesByAge.find(cr => cr.gender == Gender.Male);
        return matchesForGenderMale ?? matchesForGenderAny;
      case 'FEMALE':
        const matchesForFemale = matchesByAge.find(cr => cr.gender == Gender.Female);
        return matchesForFemale ?? matchesForGenderAny;
    }

    return undefined;
  }

  private getSpecificCutOffClinicalRange(test: NormalTest): SpecificCutOffClinicalRange | undefined {
    const ranges = test.specificCutOffClinicalRanges
      .sort((a, b) => b.resultComparer - a.resultComparer)

    return ranges.find(cr => cr);
  }

  private getSpecificCutOffClinicalRangeByResult(result: number, test: NormalTest): SpecificCutOffClinicalRange | undefined {
    if (Number.isNaN(result)) {
      return this.getSpecificCutOffClinicalRange(test);
    }

    const ranges = test.specificCutOffClinicalRanges
      .sort((a, b) => b.resultComparer - a.resultComparer);

    let bestDistance = Number.MAX_VALUE;
    let bestRange: SpecificCutOffClinicalRange | undefined;
    for (const bestRangeElement of ranges) {
      if (!SpecificCutOffClinicalRange.matches(bestRangeElement, result))
        continue;
      const distance = Math.abs(bestRangeElement.resultComparer - result);
      if (distance < bestDistance || (distance == bestDistance && bestRange !== undefined &&
        SpecificCutOffClinicalRange.getSignRank(bestRangeElement) < SpecificCutOffClinicalRange.getSignRank(bestRange))) {
        bestRange = bestRangeElement;
        bestDistance = distance;
      }
    }

    return bestRange;
  }

  public virtualCutoffWithoutMeasuringUnit(cr: SpecificCutOffClinicalRange) {
    const expression = `${this.signToString(cr)} ${cr.resultComparer}`;
    return cr.cutoff ? `${cr.cutoff}` : `Cutoff: ${expression}`;
  }

  public signToString(cr: SpecificCutOffClinicalRange) {
    return cr.sign == SpecificCutSign.LessThan ? "<" : cr.sign == SpecificCutSign.GreaterThan ? ">" :
      cr.sign == SpecificCutSign.LessThanOrEqual ? "<=" : cr.sign == SpecificCutSign.GreaterThanOrEqual ? ">=" :
        cr.sign == SpecificCutSign.Equal ? "=" : "";
  }

  private configureResult(orderInformation: OrderResultInformation, orderTest: OrderTest, test: NormalTest): { unitOfMeasure: string; flag: string, refRange: string, mode: ResultMode, clinicalRange: NumericClinicalRange | AlphaClinicalRange | TiterClinicalRange | OpenClinicalRange | SpecificCutOffClinicalRange | undefined } {
    let resultMode: ResultMode = ResultMode.None;
    let flag: string = '';
    let unitOfMeasure: string = '';
    let refRange: string = '';
    let clinicalRange: NumericClinicalRange | AlphaClinicalRange | TiterClinicalRange | OpenClinicalRange | SpecificCutOffClinicalRange | undefined = undefined;

    switch (test.resultType) {
      case ResultType.ValueValue:
      case ResultType.GreaterEqualValue:
      case ResultType.LessEqualValue:
      case ResultType.GreaterThanValue:
      case ResultType.LessThanValue: {
        resultMode = ResultMode.Numeric;
        clinicalRange = this.getNumericClinicalRange(orderInformation, test);
        switch (test.resultType) {
          case ResultType.ValueValue:
            refRange = clinicalRange ? `${clinicalRange.normalFrom}-${clinicalRange.normalTo}` : '';
            break;
          case ResultType.GreaterEqualValue:
            refRange = clinicalRange ? `>=${clinicalRange.normalTo}` : '';
            break;
          case ResultType.LessEqualValue:
            refRange = clinicalRange ? `<=${clinicalRange.normalTo}` : '';
            break;
          case ResultType.GreaterThanValue:
            refRange = clinicalRange ? `>${clinicalRange.normalTo}` : '';
            break;
          case ResultType.LessThanValue:
            refRange = clinicalRange ? `<${clinicalRange.normalTo}` : '';
            break;
        }
        unitOfMeasure = clinicalRange ? `${clinicalRange.unitOfMeasure ? clinicalRange.unitOfMeasure?.toUpperCase() : ''}` : '';
      }
        break;
      case ResultType.AlphaResults: {
        resultMode = ResultMode.Alpha;
      }
        break;
      case ResultType.OpenResultWithUnit:
      case ResultType.OpenResultWithoutUnit: {
        resultMode = ResultMode.Open;
        clinicalRange = this.getOpenClinicalRange(test);
        unitOfMeasure = clinicalRange ? clinicalRange?.unitOfMeasure : '';
      }
        break;
      case ResultType.Titer: {
        resultMode = ResultMode.Titer;
        clinicalRange = this.getTiterClinicalRange(orderInformation, test);
        refRange = clinicalRange ? `${clinicalRange.normalSign}${clinicalRange.normal}` : '';
        unitOfMeasure = clinicalRange ? clinicalRange.unitOfMeasure : '';
      }
        break;
      case ResultType.SpecificCutOff: {
        resultMode = ResultMode.CutOff;
        clinicalRange = this.getSpecificCutOffClinicalRange(test);
        refRange = clinicalRange ? this.virtualCutoffWithoutMeasuringUnit(clinicalRange) : '';
        unitOfMeasure = clinicalRange ? clinicalRange.unitOfMeasure : '';
      }
        break;
      case ResultType.None: {
        resultMode = ResultMode.None;
      }
        break;
    }

    return {
      mode: resultMode,
      flag,
      unitOfMeasure,
      refRange,
      clinicalRange
    };
  }

  public getAlphaResults(testId: number): string[] {
    const test = this.tests.find(t => t.id === testId);
    if (!test) return [];
    if (!test.alphaClinicalRanges) return [];
    return test.alphaClinicalRanges?.map(cr => cr.result);
  }

  public calculateNumericFlag(cellInfo: GridData): string {
    if (cellInfo.result === undefined || cellInfo.result === null || cellInfo.result === '') return '';
    cellInfo.flag = '';

    const test = this.tests.find(t => t.id === cellInfo.testId);

    if (!test) return '';
    if (!test.numericClinicalRanges) return '';

    const orderInfo = this.ordersInformation.find(o => o.orderId === cellInfo.orderId);
    if (!orderInfo) return '';
    const cr = this.getNumericClinicalRange(orderInfo, test) as NumericClinicalRange;
    cellInfo.flag = this.numericResultFlagFromResult(cr, Number.parseFloat(cellInfo.result), test.resultType)?.toUpperCase();
    return cellInfo.flag;
  }

  public calculateTiterFlag(cellInfo: GridData): string {
    if (cellInfo.result === undefined || cellInfo.result === null || cellInfo.result === '') return '';
    cellInfo.flag = '';

    const test = this.tests.find(t => t.id === cellInfo.testId);

    if (!test) return '';
    if (!test.titerClinicalRanges) return '';

    const orderInfo = this.ordersInformation.find(o => o.orderId === cellInfo.orderId);
    if (!orderInfo) return '';

    const cr = this.getTiterClinicalRange(orderInfo, test) as TiterClinicalRange;
    cellInfo.flag = this.titerResultFlagFromResult(cr, cellInfo.result)?.toUpperCase();

    return cellInfo.flag;
  }

  public calculateSpecificCutOffFlag(cellInfo: GridData): string {
    if (cellInfo.result === undefined || cellInfo.result === null || cellInfo.result === '') return '';
    cellInfo.flag = '';
    cellInfo.reportAs = '';
    cellInfo.cutOff = '';

    const test = this.tests.find(t => t.id === cellInfo.testId);

    if (!test) return '';
    if (!test.specificCutOffClinicalRanges) return '';

    const cr = this.getSpecificCutOffClinicalRangeByResult(Number.parseFloat(cellInfo.result), test);
    if (!cr) {
      cellInfo.flag = '';
      cellInfo.reportAs = '';
      cellInfo.cutOff = '';
      return '';
    }
    cellInfo.flag = cr.flag ? SpecificCutOffFlag[cr.flag] : '';
    cellInfo.cutOff = cr.cutoff ? `${cr.cutoff}${cr.unitOfMeasure}` : `${cr.resultComparer}${cr.unitOfMeasure}`;
    cellInfo.reportAs = !Number.isNaN(cellInfo.result) ? cr.reportAs && cr.reportAs !== '' ? cr.reportAs.toUpperCase() : cellInfo.result : '';

    return cellInfo.flag?.toUpperCase();
  }

  public calculateFlag(data: GridData): string {
    switch (data.mode) {
      case ResultMode.Titer:
        return this.calculateTiterFlag(data);
      case ResultMode.CutOff:
        return this.calculateSpecificCutOffFlag(data);
      case ResultMode.Alpha:
        return this.calculateAlphaFlagAndReferenceRange(data);
      case ResultMode.Numeric:
        return this.calculateNumericFlag(data);
      default:
        return '';
    }
  }

  private numericResultFlagFromResult(clinicalRange: NumericClinicalRange, result: number, resultType: ResultType): string {
    if (clinicalRange == null) return ResultFlagNumericConverter.convert(ResultFlagNumeric.InconsistentData);

    const normalFrom = clinicalRange.normalFrom;
    const normalTo = clinicalRange.normalTo;

    const criticalFrom = clinicalRange.criticalFrom;
    const criticalTo = clinicalRange.criticalTo;

    switch (resultType) {
      case ResultType.ValueValue:
        if (Number.isNaN(normalFrom) || Number.isNaN(normalTo))
          return ResultFlagNumericConverter.convert(ResultFlagNumeric.InconsistentData);
        if (result < normalFrom || (result == normalFrom /*&& Symbol == "<"*/))
          return (!Number.isNaN(criticalFrom) && (result < criticalFrom /*|| (result == criticalFrom /!*&& Symbol == "<"*!/)*/))
            ? ResultFlagNumericConverter.convert(ResultFlagNumeric.LowCritical)
            : ResultFlagNumericConverter.convert(ResultFlagNumeric.Low);
        if (result > normalTo || (result == normalTo /*&& Symbol == ">"*/))
          return (!Number.isNaN(criticalTo) && (result > criticalTo /*|| (result == criticalTo /!*&& Symbol == ">"*!/)*/))
            ? ResultFlagNumericConverter.convert(ResultFlagNumeric.HighCritical)
            : ResultFlagNumericConverter.convert(ResultFlagNumeric.High);
        return ResultFlagNumericConverter.convert(ResultFlagNumeric.Normal);

      case ResultType.GreaterEqualValue:
        if (Number.isNaN(normalFrom))
          return ResultFlagNumericConverter.convert(ResultFlagNumeric.InconsistentData);
        if (result < normalFrom || (result == normalFrom /*&& Symbol == "<"*/))
          return (!Number.isNaN(criticalFrom) && (result < criticalFrom /*|| (criticalFrom == result/!* && Symbol == "<"*!/)*/))
            ? ResultFlagNumericConverter.convert(ResultFlagNumeric.LowCritical)
            : ResultFlagNumericConverter.convert(ResultFlagNumeric.Low);
        return ResultFlagNumericConverter.convert(ResultFlagNumeric.Normal);

      case ResultType.GreaterThanValue:
        if (Number.isNaN(normalFrom))
          return ResultFlagNumericConverter.convert(ResultFlagNumeric.InconsistentData);
        if (result < normalFrom || (result == normalFrom /*&& (Symbol == "<" || Symbol == ">=" || Symbol == "<=" || string.IsNullOrEmpty(Symbol))*/))
          return (!Number.isNaN(criticalFrom) && (result < criticalFrom /*|| (criticalFrom == result /!*&& Symbol == "<"*!/)*/))
            ? ResultFlagNumericConverter.convert(ResultFlagNumeric.LowCritical)
            : ResultFlagNumericConverter.convert(ResultFlagNumeric.Low);
        return ResultFlagNumericConverter.convert(ResultFlagNumeric.Normal);

      case ResultType.LessEqualValue:
        if (Number.isNaN(normalTo))
          return ResultFlagNumericConverter.convert(ResultFlagNumeric.InconsistentData);
        if (result > normalTo /*|| (result == normalTo && Symbol == ">")*/)
          return (!Number.isNaN(criticalTo) && (result > criticalTo /*|| (criticalTo == result && Symbol == ">")*/))
            ? ResultFlagNumericConverter.convert(ResultFlagNumeric.HighCritical)
            : ResultFlagNumericConverter.convert(ResultFlagNumeric.High);
        return ResultFlagNumericConverter.convert(ResultFlagNumeric.Normal);


      case ResultType.LessThanValue:
        if (Number.isNaN(normalTo))
          return ResultFlagNumericConverter.convert(ResultFlagNumeric.InconsistentData);
        if (result > normalTo || (result == normalTo /*&& (Symbol == ">" || Symbol == ">=" || Symbol == "<=" || string.IsNullOrEmpty(Symbol))*/))
          return (!Number.isNaN(criticalTo) /*&& (result > criticalTo || (criticalTo == result && Symbol == ">"))*/)
            ? ResultFlagNumericConverter.convert(ResultFlagNumeric.HighCritical)
            : ResultFlagNumericConverter.convert(ResultFlagNumeric.High);
        return ResultFlagNumericConverter.convert(ResultFlagNumeric.Normal);

      default:
        return ResultFlagNumericConverter.convert(ResultFlagNumeric.InconsistentData);
    }
  }

  private titerResultFlagFromResult(clinicalRange: TiterClinicalRange, result: string) {
    if (clinicalRange == null) return ResultFlagNumericConverter.convert(ResultFlagNumeric.InconsistentData);

    const value = Number.parseInt(result.split(':')[1]);

    const normalSign = clinicalRange.normalSign;
    const normal = Number.parseInt(clinicalRange.normal.split(':')[1]);

    const criticalSign = clinicalRange.criticalSign;
    const critical = !clinicalRange.critical
      ? Number.parseInt(clinicalRange.critical.split(':')[1])
      : Number.NaN;

    let normalComparisonFlag: ResultFlagNumeric = ResultFlagNumeric.InconsistentData;
    switch (normalSign) {
      case "<":
        normalComparisonFlag = value < normal /*||(value==normal && Symbol=="<")*/ ? ResultFlagNumeric.Normal : ResultFlagNumeric.High;
        break;
      case "<=":
        normalComparisonFlag = value <= normal ? ResultFlagNumeric.Normal : ResultFlagNumeric.High;
        break;
      case ">":
        normalComparisonFlag = value > normal /*|| (value == normal && Symbol == ">")*/ ? ResultFlagNumeric.Normal : ResultFlagNumeric.Low;
        break;
      case ">=":
        normalComparisonFlag = value >= normal ? ResultFlagNumeric.Normal : ResultFlagNumeric.Low;
        break;
    }

    if (normalComparisonFlag == ResultFlagNumeric.Normal || Number.isNaN(critical)) return ResultFlagNumericConverter.convert(normalComparisonFlag);

    let criticalComparisonFlag: ResultFlagNumeric = ResultFlagNumeric.InconsistentData;
    switch (criticalSign) {
      case "<":
        criticalComparisonFlag = value < critical /*|| (value == critical.Value && Symbol == "<")*/ ? ResultFlagNumeric.LowCritical : normalComparisonFlag;
        break;
      case "<=":
        criticalComparisonFlag = value <= critical ? ResultFlagNumeric.LowCritical : normalComparisonFlag;
        break;
      case ">":
        criticalComparisonFlag = value > critical /*|| (value == critical.Value && Symbol == ">")*/ ? ResultFlagNumeric.HighCritical : normalComparisonFlag;
        break;
      case ">=":
        criticalComparisonFlag = value >= critical ? ResultFlagNumeric.HighCritical : normalComparisonFlag;
        break;
      default:
        criticalComparisonFlag = ResultFlagNumeric.InconsistentData;
        break;
    }

    return ResultFlagNumericConverter.convert(criticalComparisonFlag);
  }

  public calculateAlphaFlagAndReferenceRange(cellInfo: GridData): string {
    if (cellInfo.result === undefined || cellInfo.result === null || cellInfo.result === '') return '';
    cellInfo.flag = '';
    cellInfo.referenceRange = '';

    const test = this.tests.find(t => t.id === cellInfo.testId);

    if (!test) return '';
    if (!test.alphaClinicalRanges) return '';

    const cr = test.alphaClinicalRanges.find(cr => cr.result === cellInfo.result);
    cellInfo.flag = cr ? ResultFlag[cr.flag].toUpperCase() : '';
    cellInfo.referenceRange = cr ? cr.referenceRange.toUpperCase() : '';

    return cellInfo.flag?.toUpperCase();
  }

  //endregion

  //region cell editing methods

  setEditedValue(valueChangedEventArg: any, cellInfo: any) {
    cellInfo.setValue(valueChangedEventArg.value);
    cellInfo.data.result = valueChangedEventArg.value;
    cellInfo.data.modified = true;
  }

  //endregion

  //region dialog methods

  public openDialog() {
    this.isDialogVisible = true;
  }

  public openCommentsDialog() {
    this.isCommentsDialogVisible = true;
  }

  public onCommentsDialogClose() {
    this.isCommentsDialogVisible = false;
    this.dxDataGridComponent?.instance.beginCustomLoading("Adding comments...");
    if (this.commentsControl.value) {
      const data = this.dxDataGridComponent?.instance.getSelectedRowsData() as GridData[];
      for (const item of data) {
        if (item.editable) {
          item.comments = this.commentsControl.value;
        }
      }

      this.commentsControl.setValue('');
      this.valid = true;
    }
    this.ref.detectChanges();
    this.dxDataGridComponent?.instance.endCustomLoading();
  }

  public onDialogClose(result: string) {
    this.isDialogVisible = false;
    this.dxDataGridComponent?.instance.beginCustomLoading("Adding results...");
    if (result) {
      const data = this.dxDataGridComponent?.instance.getSelectedRowsData() as GridData[];
      for (const item of data) {
        if (item.editable) {
          item.result = result;
          item.flag = this.calculateFlag(item);
          item.modified = true;
        }
      }
      this.valid = true;
    }
    this.ref.detectChanges();
    this.dxDataGridComponent?.instance.endCustomLoading();
  }

  public isMassResultEnabled(): boolean {
    if (!this.dxDataGridComponent?.instance) return false;
    const selectedData: GridData[] = this.dxDataGridComponent?.instance.getSelectedRowsData();
    if (!selectedData || selectedData.length === 0) return false;
    return selectedData.every((item, index, array) => item?.testId === array[0].testId);
  }

  public massResultTest(): NormalTest | undefined {
    if (!this.dxDataGridComponent?.instance) return undefined;
    const selectedData: GridData[] = this.dxDataGridComponent?.instance.getSelectedRowsData();
    if (!selectedData || selectedData.length === 0) return undefined;
    const testId = selectedData.find(item => item.testId)?.testId;
    return this.tests.find(t => t.id === testId);
  }

  public onSaved() {
    this.ref.detectChanges();
  }
//endregion
}
